// Small building blocks shared by Settings, Family and Onboarding: page head, list row, avatar, bottom sheet,
// confirm sheet, switch row, copy button and a toast. Look and behaviour follow DESIGN.md (sheet like the Sound sheet).
// Glass skin: the page head is a floating glass back button over the content plus a big left-aligned title.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSkin } from '@/lib/skin';
import './ui.css';

export const SUPPORT_EMAIL = 'support@getbabyplan.com';

// ---------- icons (Lucide, stroke 2, round caps/joins, currentColor) ----------
const PATHS = {
  back: <path d="m15 18-6-6 6-6" />,
  chevron: <path d="m9 18 6-6-6-6" />,
  copy: <><rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>,
  share: <><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="m16 6-4-4-4 4" /><path d="M12 2v13" /></>,
  plusSquare: <><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M8 12h8" /><path d="M12 8v8" /></>,
  plus: <><path d="M5 12h14" /><path d="M12 5v14" /></>,
  minus: <path d="M5 12h14" />,
  up: <path d="m18 15-6-6-6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  trash: <><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  bell: <><path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" /></>,
  baby: <><path d="M9 12h.01" /><path d="M15 12h.01" /><path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" /><path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1" /></>,
  list: <><path d="M3 12h.01" /><path d="M3 18h.01" /><path d="M3 6h.01" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M8 6h13" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  ruler: <><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" /><path d="m14.5 12.5 2-2" /><path d="m11.5 9.5 2-2" /><path d="m8.5 6.5 2-2" /><path d="m17.5 15.5 2-2" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  card: <><rect width="20" height="14" x="2" y="5" rx="2" /><path d="M2 10h20" /></>,
  shield: <><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /></>,
  help: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /><path d="M12 15V3" /></>,
  more: <><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></>,
  userPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6" /><path d="M22 11h-6" /></>,
  sound: <><path d="M2 10v3" /><path d="M6 6v11" /><path d="M10 3v18" /><path d="M14 8v7" /><path d="M18 5v13" /><path d="M22 10v3" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" /><path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" /></>,
  compass: <><circle cx="12" cy="12" r="10" /><path d="m16.24 7.76-1.8 5.4a2 2 0 0 1-1.28 1.28l-5.4 1.8 1.8-5.4a2 2 0 0 1 1.28-1.28z" /></>,
  mail: <><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>,
  chat: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
  book: <><path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" /></>,
  wrench: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
  moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 20, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg className={`ico${className ? ` ${className}` : ''}`} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
      {PATHS[name]}
    </svg>
  );
}

// ---------- page head: back + title ----------
export function PageHead({ title, back = '/settings', onBack }: { title: string; back?: string; onBack?: () => void }) {
  const { t } = useTranslation('common');
  const glass = useSkin() === 'glass';
  const cls = glass ? 'back set-back lg' : 'back';
  const btn = onBack ? (
    <button className={cls} type="button" onClick={onBack} aria-label={t('actions.back')}><Icon name="back" size={24} /></button>
  ) : (
    <Link className={cls} to={back} aria-label={t('actions.back')}><Icon name="back" size={24} /></Link>
  );
  if (!glass) {
    return (
      <header className="page-head">
        {btn}
        <h1 className="page-title">{title}</h1>
      </header>
    );
  }
  // The back button floats (sticky) over the page while it scrolls; the title scrolls away with the content.
  return (
    <>
      <div className="set-top">{btn}</div>
      <header className="set-head"><h1 className="page-title set-title">{title}</h1></header>
    </>
  );
}

// ---------- grouped list rows (inset lists like iOS Settings) ----------
export function NavRow({ to, onClick, icon, label, sub, value, accent, chevron = true }: {
  to?: string; onClick?: () => void; icon?: IconName; label: ReactNode; sub?: ReactNode; value?: ReactNode; accent?: boolean; chevron?: boolean;
}) {
  const inner = (
    <>
      {icon && <span className="set-ic" aria-hidden="true"><Icon name={icon} size={19} /></span>}
      <span className="set-row-text">
        <span className="set-row-label">{label}</span>
        {sub && <span className="set-row-sub">{sub}</span>}
      </span>
      {value && <span className="set-row-value">{value}</span>}
      {chevron && <Icon name="chevron" size={16} className="set-chev" />}
    </>
  );
  const cls = `set-row${accent ? ' is-accent' : ''}`;
  return <li>{to ? <Link className={cls} to={to} onClick={onClick}>{inner}</Link> : <button className={cls} type="button" onClick={onClick}>{inner}</button>}</li>;
}

/** Round initial avatar; `tone` picks one of four soft colours so people and babies are told apart at a glance. */
export function Avatar({ name, tone = 0, size }: { name: string; tone?: number; size?: 'l' }) {
  return <span className={`set-ava t${tone % 4}${size ? ` is-${size}` : ''}`} aria-hidden="true">{(name.trim()[0] ?? '?').toUpperCase()}</span>;
}

/** Role or state tag at the end of a row: Owner, Partner, Caregiver, Pending. */
export function RoleBadge({ children, pending }: { children: ReactNode; pending?: boolean }) {
  return <span className={`set-badge${pending ? ' is-pending' : ''}`}>{children}</span>;
}

// ---------- bottom sheet (<dialog>), like the Sound sheet ----------
export function Sheet({ open, onClose, title, children, doneLabel }: { open: boolean; onClose: () => void; title: string; children: ReactNode; doneLabel?: string }) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const id = useRef(`sheet-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      d.showModal();
      d.focus(); // start on the sheet itself, not on its first button (screen readers read the labelled dialog)
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  const close = useCallback(() => {
    const d = ref.current;
    if (!d?.open) { onClose(); return; }
    const done = () => { d.classList.remove('closing'); d.close(); onClose(); (opener.current as HTMLElement | null)?.focus?.(); };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { done(); return; }
    d.classList.add('closing');
    d.addEventListener('animationend', done, { once: true });
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className="sheet set-sheet"
      tabIndex={-1}
      aria-labelledby={id}
      onCancel={(e) => { e.preventDefault(); close(); }}
      onClick={(e) => {
        const d = ref.current;
        if (!d || e.target !== d) return;
        const r = d.getBoundingClientRect();
        if (e.clientY < r.top || e.clientY > r.bottom || e.clientX < r.left || e.clientX > r.right) close();
      }}
    >
      <div className="sheet-grab" aria-hidden="true" />
      <div className="sheet-head">
        <h2 id={id}>{title}</h2>
        <button className="sheet-done" type="button" onClick={close}>{doneLabel ?? t('actions.done')}</button>
      </div>
      {open && <div className="sheet-body">{children}</div>}
    </dialog>
  );
}

/** A yes/no question in a sheet. `danger` words the confirm button plainly (no red fills: one warm accent). */
export function ConfirmSheet({ open, title, body, confirm, onConfirm, onClose, busy, error }: {
  open: boolean; title: string; body: ReactNode; confirm: string; onConfirm: () => void; onClose: () => void; busy?: boolean; error?: string | null;
}) {
  const { t } = useTranslation('common');
  return (
    <Sheet open={open} onClose={onClose} title={title} doneLabel={t('actions.cancel')}>
      <div className="stack">
        <div className="sheet-text">{body}</div>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn btn-primary btn-block" type="button" onClick={onConfirm} disabled={busy}>{confirm}</button>
        <button className="btn btn-quiet btn-block" type="button" onClick={onClose}>{t('actions.cancel')}</button>
      </div>
    </Sheet>
  );
}

// ---------- switch row ----------
export function SwitchRow({ id, label, hint, checked, disabled, onChange }: { id: string; label: string; hint?: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="switch-row">
      <label htmlFor={id} className="switch-label">
        <span>{label}</span>
        {hint && <span className="hint">{hint}</span>}
      </label>
      <input id={id} className="switch" type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}

// ---------- toast ----------
export function useToast() {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((m: string) => {
    setMsg(m);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 3200);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const node = (
    <div aria-live="polite" className="toast-host">
      {msg && <div className="toast set-toast" role="status">{msg}</div>}
    </div>
  );
  return { show, node };
}

// ---------- copy to clipboard ----------
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older iOS / insecure origins: select a hidden field and copy.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    ta.remove();
    return ok;
  }
}

export function CopyButton({ text, label, copiedLabel, onCopied }: { text: string; label: string; copiedLabel: string; onCopied?: () => void }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="chip copy-chip"
      type="button"
      onClick={async () => {
        if (await copyText(text)) { setDone(true); onCopied?.(); window.setTimeout(() => setDone(false), 2000); }
      }}
    >
      <Icon name={done ? 'check' : 'copy'} size={16} />
      <span aria-live="polite">{done ? copiedLabel : label}</span>
    </button>
  );
}

/** Download a same-origin file through fetch, so errors (rate limit, offline) can be shown instead of a raw JSON page. */
export async function downloadFile(url: string, fallbackName: string): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? `http_${res.status}`);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
